import { Context } from '@nuxt/types'
import { ApolloLink } from '@apollo/client'
import {
  InMemoryCache,
  IntrospectionFragmentMatcher
} from 'apollo-cache-inmemory'
import { Observable } from '@apollo/client/utilities'
import { AUTH_TYPE, createAuthLink } from 'aws-appsync-auth-link'
import { RetryLink } from '@apollo/client/link/retry'
import { onError } from '@apollo/client/link/error'
// eslint-disable-next-line
import ApolloLinkTimeout from 'apollo-link-timeout'
import { gql } from 'graphql-tag'
import { createSubscriptionHandshakeLink } from 'aws-appsync-subscription-link'
import { getState } from '~/lib/thing/getState'
import fragmentTypes from '~/config/fragmentTypes.json'
import { ThingDto } from '~/models/thing/Thing'
import { OpenIDConnectScheme } from '~auth/runtime'

const UNAUTHORIZED_EXCEPTION = 'UnauthorizedException'

// temporary type for apollo error since @nuxtjs/apollo is on Apollo V2
type ApolloError = {
  errorType: string
  message: string
}

export default ({ store, $envConfig, error, app }: Context) => {
  const getJwtToken = () => {
    const token = (app.$auth?.strategy as OpenIDConnectScheme)?.token.get()
    if (typeof token === 'string') return token
    return ''
  }
  const config = {
    url: $envConfig?.API_GRAPHQL,
    auth: {
      type: AUTH_TYPE.OPENID_CONNECT as const,
      jwtToken: getJwtToken
    },
    region: $envConfig.REGION,
    reconnect: true,
    disableOffline: true
  }

  /**
   * onError link doesn't accept promises, so we have to use an Observable
   * to handle the asynchronous token refresh logic.
   * If the token refresh is successful, we retry the last failed request
   * with the new token. If the refresh fails, we log the user out.
   * https://stackoverflow.com/a/51321068/21233309
   */
  const errorLink = onError((err) => {
    const { graphQLErrors, networkError, forward, operation } = err
    if (graphQLErrors) {
      const unauthorizedException = (graphQLErrors as ApolloError[]).find(
        ({ errorType }) => errorType === UNAUTHORIZED_EXCEPTION
      )
      if (unauthorizedException) {
        return new Observable((observer) => {
          app.$auth
            .refreshTokens()
            .then(() => {
              operation.setContext(({ headers = {} }) => ({
                headers: {
                  ...headers,
                  authorization: getJwtToken()
                }
              }))
            })
            .then(() => {
              const subscriber = {
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer)
              }

              // Retry last failed request
              forward(operation).subscribe(subscriber)
            })
            .catch(() => {
              // Token refresh failed, logout user
              store.dispatch('user/logout')
              observer.error(new Error('Authentication failed'))
            })
        })
      }
    }

    const hasErrorMessages = (networkError as any)?.errors?.length > 0
    if (hasErrorMessages) {
      const networkErrorMessage = (
        networkError as any
      ).errors[0].message?.toLowerCase()
      switch (networkErrorMessage) {
        case 'subscribe only available for aws appsync endpoint':
          // AppSync url not loaded properly
          error({
            statusCode: 500,
            message: app.i18n.t('warnings.invalid_appsync_url').toString()
          })
          break
        default:
          // eslint-disable-next-line no-console
          console.log(`[Network error]`, err)
      }
    }
  })

  // this generates a js error "You are calling concat on a terminating link, which will have no effect"
  // this error can be remove by change the order of those Links.
  // but if the subscription link comes before the AuthLink all query missing the jwt token.
  // known behavior since 2019: https://github.com/awslabs/aws-mobile-appsync-sdk-js/issues/448
  const link = ApolloLink.from([
    // no timeout by default, use { context: { timeout: 10000 } } in query
    new ApolloLinkTimeout(0) as ApolloLink,
    errorLink,
    new RetryLink({
      attempts: (count, _operation, error) => {
        if (!error || !error.errors || error.errors.length <= 0) return false
        const socketError = error.errors[0].message === 'Connection closed'

        // only show "live update not possible" message on second reconnect retry
        if (socketError && count === 2) {
          store.dispatch('notify/dialog', {
            message: app.i18n.t('warnings.ws_disconnected'),
            dismiss: true,
            timeout: 30000 // 30 seconds
          })
        }

        if (socketError) {
          // always retry the reestablished socket connection
          return true
        } else {
          // retry on non socket errors max 15 times
          return count <= 15
        }
      },
      delay: (count, _operation, error) => {
        const socketError = error?.errors[0]?.message === 'Connection closed'
        if (socketError) {
          // retry max 30 seconds apart in rising delays (from 1 sec 30 sec)
          return Math.min(count * 1000, 30000) * Math.random()
        } else {
          // retry max 10 seconds apart in rising delays (from 0.5 sec 10 sec)
          return Math.min(count * 500, 10000) * Math.random()
        }
      }
    }),
    createAuthLink(config),
    createSubscriptionHandshakeLink(config)
  ])

  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData: fragmentTypes
  })
  const cache = new InMemoryCache({ fragmentMatcher })

  return {
    link,
    cache,
    resolvers: {
      Thing: {
        localState: (thing: ThingDto) => {
          return getState(thing)
        }
      },
      Mutation: {
        updateThingsState: (_: any, _args: any, { cache }: any) => {
          const query = gql`
            query OrgThingsQuery {
              orgData: getRootOrg @client {
                things {
                  id
                  state
                  ts_last_seen
                  disconnect_rule
                }
              }
            }
          `
          try {
            const data = cache.readQuery({ query })
            for (const thing of data?.orgData.things) {
              const localState = getState(thing)
              if (localState !== thing.localState) {
                cache.writeData({
                  id: `${thing.__typename}:${thing.id}`,
                  data: {
                    localState
                  }
                })
              }
            }
          } catch (e) {}
          return null
        }
      }
    }
  }
}
